import * as THREE from 'three';
import {
  CANNONS, CHANNELS, DECK, DECK_X_MAX, DECK_X_MIN, HATCHES, deckHalfWidth, deckY, gunportRect, halfBreadth, halfBreadthXY,
  keelY, sheerY, stemX, sternX, yFromT, transomFraction,
} from '../../data/ship';
import { Batch, box, rod, slab, sweep, tube, V } from './geom';

// Hull, decks and joinery, generated from the same hull-line functions the
// buoyancy uses, so what you see is what floats.

let NU = 200;
let NT = 72;

/** Coarse hull shell (both sides + transom) for distant ships. */
export function lowPolyHull(): THREE.BufferGeometry[] {
  const [nu, nt] = [NU, NT];
  NU = 40;
  NT = 14;
  const out = [hullShell(1), hullShell(-1), transom()];
  NU = nu;
  NT = nt;
  return out;
}

/** One coarse deck surface over all three decks, closing the low-poly hull. */
export function lowPolyDeck(): THREE.BufferGeometry {
  return deckSurface(DECK_X_MIN, DECK_X_MAX, deckY, 1.0, 4);
}

/** Gunport rectangles (both sides share them), for shader cut-outs. */
export function portRects(): THREE.Vector4[] {
  const seen = new Set<number>();
  const out: THREE.Vector4[] = [];
  for (const c of CANNONS) {
    if (seen.has(c.x)) continue;
    seen.add(c.x);
    const r = gunportRect(c);
    out.push(new THREE.Vector4(r.x0, r.x1, r.y0, r.y1));
  }
  while (out.length < 16) out.push(new THREE.Vector4(1e4, 1e4, 1e4, 1e4));
  return out;
}

/** Outer shell (starboard or port). */
function hullShell(side: 1 | -1): THREE.BufferGeometry {
  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  const girth: number[] = new Array((NU + 1) * (NT + 1)).fill(0);
  const P = (i: number, j: number) => {
    const t = j / NT;
    const u = i / NU;
    const x = sternX(t) + u * (stemX(t) - sternX(t));
    const y = yFromT(x, t);
    const hb = Math.max(halfBreadth(x, t), 0.2);
    return V(x, y, hb * side);
  };
  const pts: THREE.Vector3[] = [];
  for (let j = 0; j <= NT; j++) for (let i = 0; i <= NU; i++) pts.push(P(i, j));
  for (let i = 0; i <= NU; i++) {
    for (let j = 1; j <= NT; j++) {
      girth[j * (NU + 1) + i] = girth[(j - 1) * (NU + 1) + i]! + pts[j * (NU + 1) + i]!.distanceTo(pts[(j - 1) * (NU + 1) + i]!);
    }
  }
  for (let j = 0; j <= NT; j++) {
    for (let i = 0; i <= NU; i++) {
      const p = pts[j * (NU + 1) + i]!;
      pos.push(p.x, p.y, p.z);
      uv.push(p.x, girth[j * (NU + 1) + i]!);
    }
  }
  const row = NU + 1;
  for (let j = 0; j < NT; j++) {
    for (let i = 0; i < NU; i++) {
      const a = j * row + i, b = a + 1, c = a + row, d = c + 1;
      if (side > 0) idx.push(a, b, c, b, d, c);
      else idx.push(a, c, b, b, c, d);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

/** Flat transom across the counter above the sternpost. */
function transom(): THREE.BufferGeometry {
  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  const t0 = 0.36, NTT = 30, NS = 20;
  for (let j = 0; j <= NTT; j++) {
    const t = t0 + (1 - t0) * (j / NTT);
    const x = sternX(t);
    const y = yFromT(x, t);
    const hb = Math.max(halfBreadth(x, t), 0.2);
    for (let s = 0; s <= NS; s++) {
      const z = (s / NS * 2 - 1) * hb;
      pos.push(x, y, z);
      uv.push(z, y);
    }
  }
  for (let j = 0; j < NTT; j++) {
    for (let s = 0; s < NS; s++) {
      const a = j * (NS + 1) + s, b = a + 1, c = a + NS + 1, d = c + 1;
      idx.push(a, b, c, b, d, c);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

/** Inner face of the bulwarks, from the deck up to the rail. */
function bulwarkInner(side: 1 | -1): THREE.BufferGeometry {
  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  const xs: number[] = [];
  // Carried right forward to the stem, where the deck has run out, so the
  // bows are closed in from inside too.
  let lastDeck = DECK_X_MIN;
  for (let x = DECK_X_MIN + 0.02; x <= stemX(1); x += 0.2) {
    if (deckHalfWidth(x) > 0.1) {
      xs.push(x);
      lastDeck = x;
    } else if (x > 0 && halfBreadthXY(x, sheerY(x) - 0.05) - DECK.sideThickness > 0.02) xs.push(x);
  }
  const NV = 8;
  for (const x of xs) {
    const y0 = deckY(Math.min(x, lastDeck)) - 0.05, y1 = sheerY(x) - 0.02;
    for (let k = 0; k <= NV; k++) {
      const y = y0 + (y1 - y0) * (k / NV);
      const hb = Math.max(halfBreadthXY(x, y), 0.3);
      pos.push(x, y, side * (hb - DECK.sideThickness));
      uv.push(x, y);
    }
  }
  for (let i = 0; i < xs.length - 1; i++) {
    for (let k = 0; k < NV; k++) {
      const a = i * (NV + 1) + k, b = a + 1, c = a + NV + 1, d = c + 1;
      // Faces point inboard (toward the deck), where they're seen from.
      if (side > 0) idx.push(a, b, c, b, d, c);
      else idx.push(a, c, b, b, c, d);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

/** Deck surface between x0 and x1 with camber. */
function deckSurface(x0: number, x1: number, yOf: (x: number) => number, step = 0.25, NZ = 12): THREE.BufferGeometry {
  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  const xs: number[] = [];
  for (let x = x0; x < x1; x += step) if (deckHalfWidth(x) > 0.05) xs.push(x);
  xs.push(x1);
  for (const x of xs) {
    const hw = Math.max(0.05, deckHalfWidth(x)) + 0.06;
    for (let k = 0; k <= NZ; k++) {
      const s = k / NZ * 2 - 1;
      const z = s * hw;
      const y = yOf(x) + 0.12 * (1 - s * s);
      pos.push(x, y, z);
      uv.push(x, z);
    }
  }
  for (let i = 0; i < xs.length - 1; i++) {
    for (let k = 0; k < NZ; k++) {
      const a = i * (NZ + 1) + k, b = a + 1, c = a + NZ + 1, d = c + 1;
      idx.push(a, b, c, b, d, c);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

function railPath(side: 1 | -1, inset: number): { path: THREE.Vector3[]; sides: THREE.Vector3[] } {
  const path: THREE.Vector3[] = [];
  const sides: THREE.Vector3[] = [];
  for (let x = sternX(1) + 0.05; x <= stemX(1) - 0.02; x += 0.3) {
    const y = sheerY(x);
    const hb = halfBreadthXY(x, y - 0.01);
    if (hb < 0) continue;
    path.push(V(x, y, side * (hb - inset)));
    sides.push(V(0, 0, side));
  }
  return { path, sides };
}

/** Horizontal band on the hull at height y (wales, mouldings). */
function strake(side: 1 | -1, y: number, height: number, out: number, x0 = -18, x1 = 19.5): THREE.BufferGeometry {
  const path: THREE.Vector3[] = [];
  const sides: THREE.Vector3[] = [];
  for (let x = x0; x <= x1; x += 0.25) {
    const hb = halfBreadthXY(x, y);
    if (hb < 0.4) continue;
    // Follow the hull's local slope in plan.
    const hb2 = halfBreadthXY(x + 0.05, y);
    const dz = (hb2 - hb) / 0.05;
    const n = V(-dz * side, 0, side).normalize();
    path.push(V(x, y, side * hb));
    sides.push(n);
  }
  const prof = [
    new THREE.Vector2(-0.02, -height / 2),
    new THREE.Vector2(out, -height / 2 + 0.03),
    new THREE.Vector2(out + 0.02, 0),
    new THREE.Vector2(out, height / 2 - 0.03),
    new THREE.Vector2(-0.02, height / 2),
  ];
  return sweep(prof, path, sides);
}

export function buildHull(batch: Batch): { rudder: THREE.BufferGeometry; rudderPivot: THREE.Vector3; rudderAxis: THREE.Vector3 } {
  batch.add('hull', hullShell(1));
  batch.add('hull', hullShell(-1));
  batch.add('hull', transom());
  batch.add('bulwark', bulwarkInner(1));
  batch.add('bulwark', bulwarkInner(-1));
  // Inside of the taffrail across the stern, as wide at every height as the
  // bulwarks it meets (the quarters flare out toward the rail).
  {
    const x = sternX(0.92) + 0.3;
    const y0 = deckY(x) - 0.05, y1 = sheerY(x) - 0.02;
    const pos: number[] = [];
    const uv: number[] = [];
    const idx: number[] = [];
    const NV = 6;
    for (let k = 0; k <= NV; k++) {
      const y = y0 + ((y1 - y0) * k) / NV;
      const w = Math.max(halfBreadthXY(x, y), 0.3) - DECK.sideThickness + 0.04;
      pos.push(x, y, -w, x, y, w);
      uv.push(-w, y, w, y);
    }
    for (let k = 0; k < NV; k++) {
      const a = k * 2, b = a + 1, c = a + 2, d = a + 3;
      // Facing forward, toward the deck it's seen from.
      idx.push(a, c, b, b, c, d);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    g.setIndex(idx);
    g.computeVertexNormals();
    batch.add('bulwark', g);
  }

  // Cap rail along the top of the bulwarks.
  for (const side of [1, -1] as const) {
    const { path, sides } = railPath(side, DECK.sideThickness / 2);
    const prof = [new THREE.Vector2(-0.2, -0.04), new THREE.Vector2(0.2, -0.04), new THREE.Vector2(0.2, 0.05), new THREE.Vector2(-0.2, 0.05)];
    batch.add('blackWood', sweep(prof, path, sides));
    // Main and channel wales, and a moulding under the rail.
    batch.add('blackWood', strake(side, 0.55, 0.3, 0.12, -17.3, 17.2));
    batch.add('blackWood', strake(side, 1.35, 0.28, 0.11, -17.6, 17.8));
    batch.add('blackWood', strake(side, 2.18, 0.1, 0.06, -17.8, 18.8));
  }
  // Taffrail cap across the stern.
  {
    const x = sternX(1) + 0.12;
    const y = sheerY(x) + 0.03;
    const hb = halfBreadth(sternX(1), 1);
    const g = box(0.4, 0.12, hb * 2 + 0.2);
    g.translate(x, y, 0);
    batch.add('blackWood', g);
    // Carved taffrail board.
    const shape = [
      new THREE.Vector2(-hb * 0.7, 0), new THREE.Vector2(hb * 0.7, 0), new THREE.Vector2(hb * 0.55, 0.22), new THREE.Vector2(hb * 0.25, 0.34),
      new THREE.Vector2(0, 0.4), new THREE.Vector2(-hb * 0.25, 0.34), new THREE.Vector2(-hb * 0.55, 0.22),
    ];
    const tb = slab(shape, 0.1);
    tb.rotateY(Math.PI / 2);
    tb.translate(x - 0.05, y + 0.06, 0);
    batch.add('blackWood', tb);
    // Gilt scroll along its edge.
    const edge: THREE.Vector3[] = shape.map((p) => V(x - 0.12, y + 0.06 + p.y, p.x));
    batch.add('gilt', tube([...edge, edge[0]!], 0.025, 5, 8));
  }

  // Keel, stem and sternpost.
  {
    const keel: THREE.Vector3[] = [];
    const sides: THREE.Vector3[] = [];
    for (let x = sternX(0); x <= stemX(0.08); x += 0.4) {
      keel.push(V(x, keelY(x) - 0.2, 0));
      sides.push(V(0, 0, 1));
    }
    const prof = [new THREE.Vector2(-0.22, -0.22), new THREE.Vector2(0.22, -0.22), new THREE.Vector2(0.22, 0.25), new THREE.Vector2(-0.22, 0.25)];
    batch.add('timber', sweep(prof, keel, sides));
    const stem: THREE.Vector3[] = [];
    const ss: THREE.Vector3[] = [];
    for (let t = 0; t <= 1.0001; t += 0.05) {
      const x = stemX(t);
      stem.push(V(x + 0.05, yFromT(x, t), 0));
      ss.push(V(0, 0, 1));
    }
    const top = stem[stem.length - 1]!;
    stem.push(V(top.x + 0.1, top.y + 0.8, 0));
    ss.push(V(0, 0, 1));
    batch.add('timber', sweep([new THREE.Vector2(-0.23, -0.18), new THREE.Vector2(0.23, -0.18), new THREE.Vector2(0.23, 0.18), new THREE.Vector2(-0.23, 0.18)], stem, ss));
    const sp: THREE.Vector3[] = [];
    const sps: THREE.Vector3[] = [];
    for (let t = 0; t <= 0.42; t += 0.03) {
      const x = sternX(t);
      sp.push(V(x - 0.05, yFromT(x, t), 0));
      sps.push(V(0, 0, 1));
    }
    batch.add('timber', sweep([new THREE.Vector2(-0.22, -0.16), new THREE.Vector2(0.22, -0.16), new THREE.Vector2(0.22, 0.16), new THREE.Vector2(-0.22, 0.16)], sp, sps));
  }

  // Knee of the head (cutwater) carrying the figurehead.
  {
    const pts: THREE.Vector2[] = [];
    for (let t = 0.85; t >= 0.29; t -= 0.04) {
      const x = stemX(t);
      pts.push(new THREE.Vector2(x - 0.1, yFromT(x, t)));
    }
    for (const [x, y] of [[18.6, 0.2], [20.3, 1.3], [21.3, 2.5], [21.85, 3.45], [21.55, 4.0], [20.6, 4.15]] as const) pts.push(new THREE.Vector2(x, y));
    const knee = slab(pts, 0.36, 0.03);
    batch.add('blackWood', knee);
    // Trailboards with gilt scrollwork along the knee.
    for (const side of [1, -1]) {
      const tb = tube([V(18.7, 1.9, side * 0.2), V(19.8, 2.4, side * 0.2), V(20.8, 3.0, side * 0.2), V(21.4, 3.5, side * 0.2)], 0.05, 6);
      batch.add('gilt', tb);
      // Head rails sweeping from the bow to the figurehead.
      for (const [yb, k] of [[3.6, 1], [3.1, 0.7]] as const) {
        const hr = tube([V(18.2, yb + 0.5, side * 1.6 * k), V(19.4, yb + 0.25, side * 1.1 * k), V(20.6, yb - 0.05, side * 0.6 * k), V(21.4, yb - 0.1, side * 0.22)], 0.06, 7);
        batch.add('blackWood', hr);
      }
    }
    // Figurehead: a gilded scroll (fiddlehead) volute.
    const vol: THREE.Vector3[] = [];
    for (let a = 0; a < Math.PI * 3.2; a += 0.15) {
      const r = 0.42 * Math.exp(-a * 0.2);
      vol.push(V(21.7 + Math.cos(a) * r * 0.9 - 0.1, 3.85 + Math.sin(a) * r, 0));
    }
    batch.add('gilt', tube(vol, 0.09, 10, 10));
    for (const side of [1, -1]) {
      const leaf = tube([V(21.2, 3.2, side * 0.21), V(21.6, 3.5, side * 0.22), V(21.8, 3.9, side * 0.2), V(21.6, 4.2, side * 0.17)], 0.045, 6);
      batch.add('gilt', leaf);
    }
  }

  // Decks.
  batch.add('deck', deckSurface(DECK.quarterFrontX - 0.02, DECK.foreBackX + 0.02, (x) => DECK.mainY + 0.0017 * x * x));
  batch.add('deck', deckSurface(DECK_X_MIN, DECK.quarterFrontX + 0.02, deckY));
  batch.add('deck', deckSurface(DECK.foreBackX - 0.02, DECK_X_MAX, deckY));

  // Break of the quarterdeck: bulkhead with cabin doors and windows.
  {
    const x = DECK.quarterFrontX;
    const y0 = DECK.mainY + 0.0017 * x * x, y1 = DECK.quarterY + 0.1;
    const hw = deckHalfWidth(x) + 0.05;
    const doors = [-1.5, 1.5];
    const dw = 0.9, dh = 1.75;
    const segs: [number, number][] = [];
    let z = -hw;
    for (const dz of doors) {
      segs.push([z, dz - dw / 2]);
      z = dz + dw / 2;
    }
    segs.push([z, hw]);
    for (const [za, zb] of segs) {
      const g = box(0.14, y1 - y0, zb - za);
      g.translate(x, (y0 + y1) / 2, (za + zb) / 2);
      batch.add('redWood', g);
    }
    for (const dz of doors) {
      const lintel = box(0.14, y1 - y0 - dh, dw);
      lintel.translate(x, y0 + dh + (y1 - y0 - dh) / 2, dz);
      batch.add('redWood', lintel);
      // Doors ajar into the dark great cabin.
      const recess = box(0.05, dh, dw);
      recess.translate(x - 0.4, y0 + dh / 2, dz);
      batch.add('dark', recess);
      const door = box(0.06, dh - 0.05, dw * 0.95);
      door.translate(0, 0, -dw * 0.47);
      door.rotateY(dz > 0 ? 1.1 : -1.1);
      door.translate(x - 0.02, y0 + dh / 2, dz + dw * 0.47 * (dz > 0 ? 1 : -1));
      batch.add('blackWood', door);
      // Door frames.
      for (const s of [-1, 1]) {
        const post = box(0.18, dh, 0.08);
        post.translate(x + 0.02, y0 + dh / 2, dz + s * (dw / 2 + 0.04));
        batch.add('gilt', post);
      }
    }
    // Small cabin windows either side of the doors.
    for (const dz of [-3.1, 0, 3.1]) {
      const wnd = box(0.03, 0.42, 0.5);
      wnd.translate(x + 0.08, y0 + 1.25, dz);
      batch.add('glass', wnd);
      const frame = box(0.05, 0.5, 0.58);
      frame.translate(x + 0.07, y0 + 1.25, dz);
      batch.add('gilt', frame);
    }
    // Deck beam along the break.
    const beam = box(0.3, 0.22, hw * 2);
    beam.translate(x + 0.05, y1 - 0.12, 0);
    batch.add('blackWood', beam);
  }
  // Break of the forecastle.
  {
    const x = DECK.foreBackX;
    const y0 = DECK.mainY + 0.0017 * x * x, y1 = DECK.foreY + 0.1;
    const hw = deckHalfWidth(x) + 0.05;
    const g = box(0.14, y1 - y0, hw * 2);
    g.translate(x, (y0 + y1) / 2, 0);
    batch.add('redWood', g);
    const beam = box(0.28, 0.2, hw * 2);
    beam.translate(x - 0.05, y1 - 0.1, 0);
    batch.add('blackWood', beam);
  }

  // Hatches: coamings, gratings, dark hold beneath.
  for (const h of HATCHES) {
    const y = DECK.mainY + 0.0017 * h.x * h.x + 0.1;
    const hold = box(h.hx * 2, 1.2, h.hz * 2);
    hold.translate(h.x, y - 0.65, 0);
    batch.add('dark', hold);
    for (const [dx, dz, w, d] of [[0, h.hz, h.hx * 2 + 0.24, 0.12], [0, -h.hz, h.hx * 2 + 0.24, 0.12], [h.hx, 0, 0.12, h.hz * 2], [-h.hx, 0, 0.12, h.hz * 2]] as const) {
      const c = box(w, 0.34, d);
      c.translate(h.x + dx, y + 0.12, dz);
      batch.add('timber', c);
    }
    if (h.grating) {
      for (let gx = -h.hx + 0.06; gx < h.hx; gx += 0.13) {
        const b = box(0.045, 0.06, h.hz * 2);
        b.translate(h.x + gx, y + 0.24, 0);
        batch.add('timber', b);
      }
      for (let gz = -h.hz + 0.06; gz < h.hz; gz += 0.13) {
        const b = box(h.hx * 2, 0.04, 0.035);
        b.translate(h.x, y + 0.25, gz);
        batch.add('timber', b);
      }
    } else {
      const lid = box(h.hx * 2, 0.08, h.hz * 2);
      lid.translate(h.x, y + 0.3, 0);
      batch.add('blackWood', lid);
    }
  }

  // Gunport linings and lids hinged open.
  for (const c of CANNONS) {
    const r = gunportRect(c);
    const yc = (r.y0 + r.y1) / 2;
    const hbO = halfBreadthXY(c.x, yc);
    const z0 = c.side * (hbO - DECK.sideThickness - 0.02), z1 = c.side * (hbO + 0.03);
    const depth = Math.abs(z1 - z0);
    const zc = (z0 + z1) / 2;
    const w = r.x1 - r.x0, h = r.y1 - r.y0;
    for (const [dx, dy, sw, sh] of [[0, h / 2, w + 0.1, 0.05], [0, -h / 2, w + 0.1, 0.05], [w / 2, 0, 0.05, h], [-w / 2, 0, 0.05, h]] as const) {
      const lg = box(sw, sh, depth);
      lg.translate(c.x + dx, yc + dy, zc);
      batch.add('redWood', lg);
    }
    // Lid: hinged at the top, swung out and up.
    const lid = box(w + 0.08, h + 0.06, 0.08);
    lid.translate(0, -(h + 0.06) / 2, 0.04 * c.side);
    lid.rotateX(-c.side * 1.25);
    lid.translate(c.x, r.y1 + 0.03, c.side * (hbO + 0.06));
    batch.add('blackWood', lid);
  }

  // Channels: ledges outboard at each mast for the shrouds.
  for (const [xc, len] of Object.values(CHANNELS)) {
    for (const side of [1, -1] as const) {
      const y = sheerY(xc) - 0.55;
      const hb = halfBreadthXY(xc, y);
      const g = box(len, 0.1, 0.62);
      g.translate(xc, y, side * (hb + 0.29));
      batch.add('blackWood', g);
    }
  }

  // Stern: great cabin windows, carved frames, quarter galleries.
  {
    const nWin = 5;
    const yW = DECK.mainY + 1.45;
    const t = 0.72;
    const x = sternX(t) - 0.02;
    const hbT = halfBreadth(sternX(t), t);
    const span = hbT * 1.6;
    for (let i = 0; i < nWin; i++) {
      const z = -span / 2 + (i + 0.5) * (span / nWin);
      const g = box(0.04, 0.8, span / nWin - 0.12);
      g.translate(x - 0.03, yW, z);
      batch.add('glass', g);
      const f = box(0.08, 0.9, span / nWin - 0.03);
      f.translate(x - 0.005, yW, z);
      batch.add('gilt', f);
    }
    // Mouldings across the counter.
    for (const [yy, hh] of [[yW - 0.6, 0.12], [yW + 0.58, 0.1], [DECK.quarterY + 0.5, 0.08]] as const) {
      const tt = (yy - keelY(-18)) / (sheerY(-18) - keelY(-18));
      const hx = sternX(Math.min(1, tt));
      const hbm = Math.max(0.3, halfBreadth(hx, Math.min(1, tt)));
      const g = box(0.16, hh, hbm * 2 + 0.1);
      g.translate(hx - 0.04, yy, 0);
      batch.add('gilt', g);
    }
    // Upper windows lighting the captain's quarters under the poop.
    for (let i = 0; i < 4; i++) {
      const t2 = 0.9;
      const x2 = sternX(t2) - 0.02;
      const hb2 = halfBreadth(sternX(t2), t2) * 1.4;
      const z = -hb2 / 2 + (i + 0.5) * (hb2 / 4);
      const g = box(0.04, 0.45, hb2 / 4 - 0.14);
      g.translate(x2 - 0.03, DECK.quarterY + 0.95, z);
      batch.add('glass', g);
    }
    // Quarter galleries.
    for (const side of [1, -1] as const) {
      const xg = -15.9;
      const yg = DECK.mainY + 1.3;
      const hb = halfBreadthXY(xg, yg);
      const gal = new THREE.SphereGeometry(1, 16, 10, 0, Math.PI, 0, Math.PI);
      gal.scale(1.4, 1.3, 0.55);
      gal.rotateY(side > 0 ? 0 : Math.PI);
      gal.translate(xg, yg, side * (hb - 0.05));
      batch.add('blackWood', gal);
      for (let i = 0; i < 3; i++) {
        const w = box(0.34, 0.5, 0.05);
        w.translate(xg - 0.45 + i * 0.45, yg + 0.1, side * (hb + 0.47));
        batch.add('glass', w);
      }
      const cap = new THREE.ConeGeometry(0.9, 0.9, 12, 1, false, 0, Math.PI);
      cap.scale(1.3, 1, 0.6);
      cap.rotateY(side > 0 ? 0 : Math.PI);
      cap.translate(xg, yg + 1.72, side * (hb - 0.05));
      batch.add('gilt', cap);
    }
  }

  // Rudder hung on the sternpost.
  const t0 = 0.02, t1 = 0.46;
  const a = V(sternX(t0), yFromT(sternX(t0), t0), 0);
  const b = V(sternX(t1), yFromT(sternX(t1), t1), 0);
  const axis = new THREE.Vector3().subVectors(b, a).normalize();
  const shape: THREE.Vector2[] = [];
  // Built in the pivot frame: x aft (negative), y along the post.
  const L = a.distanceTo(b);
  shape.push(new THREE.Vector2(0, 0), new THREE.Vector2(-1.25, 0.1), new THREE.Vector2(-1.15, L * 0.55), new THREE.Vector2(-0.6, L), new THREE.Vector2(0, L + 0.8));
  const rudder = slab(shape, 0.34, 0.02);
  void transomFraction;
  return { rudder, rudderPivot: a, rudderAxis: axis };
}

/** Small fittings shared with fittings.ts. */
export function ladder(batch: Batch, x0: number, y0: number, x1: number, y1: number, z: number, width = 0.7): void {
  for (const s of [-1, 1]) batch.add('timber', rod(V(x0, y0, z + s * width / 2), V(x1, y1, z + s * width / 2), 0.05, 0.05, 6));
  const n = Math.max(3, Math.round((y1 - y0) / 0.28));
  for (let i = 1; i < n; i++) {
    const t = i / n;
    const x = x0 + (x1 - x0) * t, y = y0 + (y1 - y0) * t;
    const step = box(0.26, 0.05, width);
    step.translate(x, y, z);
    batch.add('timber', step);
  }
}
